import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, getErrorMessage } from '../services/api';
import {
  ExchangeCredential, CreateExchangeCredentialRequest,
  UserSettings, UpdateDefaultSettingsRequest, UpdateRiskSettingsRequest, UpdateNotificationSettingsRequest,
  EmailServiceConfig, EmailServiceType, CreateEmailServiceConfigRequest
} from '../types';

type TabType = 'credentials' | 'defaults' | 'risk' | 'notifications' | 'email';

export function Settings() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('credentials');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [credentials, setCredentials] = useState<ExchangeCredential[]>([]);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [emailConfigs, setEmailConfigs] = useState<EmailServiceConfig[]>([]);
  const [emailServiceTypes, setEmailServiceTypes] = useState<EmailServiceType[]>([]);

  // Form states for new credential
  const [showNewCredentialForm, setShowNewCredentialForm] = useState(false);
  const [newCredential, setNewCredential] = useState<CreateExchangeCredentialRequest>({
    exchangeName: 'Coinbase',
    displayName: '',
    apiKeyName: '',
    privateKey: '',
  });

  // Form states for new email service config
  const [showNewEmailConfigForm, setShowNewEmailConfigForm] = useState(false);
  const [newEmailConfig, setNewEmailConfig] = useState<CreateEmailServiceConfigRequest>({
    serviceType: 'Mailgun',
    displayName: '',
    apiKey: '',
    domain: '',
    fromEmail: '',
    fromName: '',
    region: 'US',
  });
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testingConfigId, setTestingConfigId] = useState<number | null>(null);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');
      const [creds, userSettings, emailCfgs, emailTypes] = await Promise.all([
        api.getExchangeCredentials(),
        api.getSettings().catch(() => null),
        api.getEmailServiceConfigs().catch(() => []),
        api.getEmailServiceTypes().catch(() => []),
      ]);
      setCredentials(creds);
      setSettings(userSettings);
      setEmailConfigs(emailCfgs);
      setEmailServiceTypes(emailTypes);
    } catch (err) {
      setError(`Failed to load settings: ${getErrorMessage(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const handleCreateCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      await api.createExchangeCredential(newCredential);
      setSuccess('Credential created successfully');
      setShowNewCredentialForm(false);
      setNewCredential({ exchangeName: 'Coinbase', displayName: '', apiKeyName: '', privateKey: '' });
      await loadData();
    } catch (err) {
      setError(`Failed to create credential: ${getErrorMessage(err)}`);
    }
  };

  const handleActivate = async (id: number) => {
    try {
      setError('');
      await api.activateExchangeCredential(id);
      await api.reinitializeExchange();
      setSuccess('Credential activated');
      await loadData();
    } catch (err) {
      setError(`Failed to activate: ${getErrorMessage(err)}`);
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      setError('');
      await api.deactivateExchangeCredential(id);
      setSuccess('Credential deactivated');
      await loadData();
    } catch (err) {
      setError(`Failed to deactivate: ${getErrorMessage(err)}`);
    }
  };

  const handleDeleteCredential = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) return;
    try {
      setError('');
      await api.deleteExchangeCredential(id);
      setSuccess('Credential deleted');
      await loadData();
    } catch (err) {
      setError(`Failed to delete: ${getErrorMessage(err)}`);
    }
  };

  const handleSaveDefaults = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;
    try {
      setError('');
      const data: UpdateDefaultSettingsRequest = {
        defaultInvestment: settings.defaultInvestment,
        defaultIntervalHours: settings.defaultIntervalHours,
        defaultQuoteCurrency: settings.defaultQuoteCurrency,
      };
      await api.updateDefaultSettings(data);
      setSuccess('Default settings saved');
    } catch (err) {
      setError(`Failed to save: ${getErrorMessage(err)}`);
    }
  };

  const handleSaveRisk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;
    try {
      setError('');
      const data: UpdateRiskSettingsRequest = {
        maxPositionSize: settings.maxPositionSize,
        defaultStopLossPercent: settings.defaultStopLossPercent,
        maxLeverage: settings.maxLeverage,
        enableRiskChecks: settings.enableRiskChecks,
      };
      await api.updateRiskSettings(data);
      setSuccess('Risk settings saved');
    } catch (err) {
      setError(`Failed to save: ${getErrorMessage(err)}`);
    }
  };

  const handleSaveNotifications = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;
    try {
      setError('');
      const data: UpdateNotificationSettingsRequest = {
        notifyOnOrderFill: settings.notifyOnOrderFill,
        notifyOnError: settings.notifyOnError,
        notifyOnPositionClose: settings.notifyOnPositionClose,
        notificationEmail: settings.notificationEmail,
        webhookUrl: settings.webhookUrl,
      };
      await api.updateNotificationSettings(data);
      setSuccess('Notification settings saved');
    } catch (err) {
      setError(`Failed to save: ${getErrorMessage(err)}`);
    }
  };

  // Email Service handlers
  const handleCreateEmailConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      await api.createEmailServiceConfig(newEmailConfig);
      setSuccess('Email service configuration created');
      setShowNewEmailConfigForm(false);
      setNewEmailConfig({
        serviceType: 'Mailgun',
        displayName: '',
        apiKey: '',
        domain: '',
        fromEmail: '',
        fromName: '',
        region: 'US',
      });
      await loadData();
    } catch (err) {
      setError(`Failed to create email config: ${getErrorMessage(err)}`);
    }
  };

  const handleActivateEmailConfig = async (id: number) => {
    try {
      setError('');
      await api.activateEmailServiceConfig(id);
      setSuccess('Email service activated');
      await loadData();
    } catch (err) {
      setError(`Failed to activate: ${getErrorMessage(err)}`);
    }
  };

  const handleDeactivateEmailConfig = async (id: number) => {
    try {
      setError('');
      await api.deactivateEmailServiceConfig(id);
      setSuccess('Email service deactivated');
      await loadData();
    } catch (err) {
      setError(`Failed to deactivate: ${getErrorMessage(err)}`);
    }
  };

  const handleDeleteEmailConfig = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this email service configuration?')) return;
    try {
      setError('');
      await api.deleteEmailServiceConfig(id);
      setSuccess('Email service deleted');
      await loadData();
    } catch (err) {
      setError(`Failed to delete: ${getErrorMessage(err)}`);
    }
  };

  const handleTestEmail = async (configId: number) => {
    if (!testEmailAddress) {
      setError('Please enter an email address to test');
      return;
    }
    try {
      setError('');
      setTestingConfigId(configId);
      const result = await api.testEmailService(configId, testEmailAddress);
      if (result.success) {
        setSuccess(`Test email sent successfully to ${testEmailAddress}`);
      } else {
        setError(`Test email failed: ${result.message ?? 'Unknown error'}${result.errorDetails ? ` - ${result.errorDetails}` : ''}`);
      }
    } catch (err) {
      setError(`Failed to send test email: ${getErrorMessage(err)}`);
    } finally {
      setTestingConfigId(null);
    }
  };

  const renderCredentialsTab = () => (
    <div>
      <div style={styles.tabHeader}>
        <h3 style={styles.tabTitle}>Exchange Credentials</h3>
        <button onClick={() => setShowNewCredentialForm(true)} style={styles.addButton}>
          + Add Credential
        </button>
      </div>

      {showNewCredentialForm && (
        <form onSubmit={handleCreateCredential} style={styles.form}>
          <h4 style={styles.formTitle}>New Exchange Credential</h4>
          <div style={styles.formGroup}>
            <label style={styles.label}>Exchange</label>
            <select
              value={newCredential.exchangeName}
              onChange={e => setNewCredential({ ...newCredential, exchangeName: e.target.value })}
              style={styles.select}
            >
              <option value="Coinbase">Coinbase</option>
              <option value="Kraken">Kraken</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Display Name</label>
            <input
              type="text"
              value={newCredential.displayName}
              onChange={e => setNewCredential({ ...newCredential, displayName: e.target.value })}
              style={styles.input}
              placeholder="e.g., My Coinbase Account"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>API Key Name</label>
            <input
              type="text"
              value={newCredential.apiKeyName}
              onChange={e => setNewCredential({ ...newCredential, apiKeyName: e.target.value })}
              style={styles.input}
              placeholder="API Key Name or ID"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Private Key</label>
            <textarea
              value={newCredential.privateKey}
              onChange={e => setNewCredential({ ...newCredential, privateKey: e.target.value })}
              style={styles.textarea}
              placeholder="Paste your private key here..."
              rows={4}
              required
            />
          </div>
          <div style={styles.formActions}>
            <button type="submit" style={styles.saveButton}>Create</button>
            <button type="button" onClick={() => setShowNewCredentialForm(false)} style={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {credentials.length === 0 ? (
        <div style={styles.empty}>No exchange credentials configured</div>
      ) : (
        <div style={styles.credentialsList}>
          {credentials.map(cred => (
            <div key={cred.exchangeCredentialId} style={styles.credentialCard}>
              <div style={styles.credentialHeader}>
                <span style={styles.credentialName}>{cred.displayName}</span>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: cred.isActive ? '#4caf50' : '#9e9e9e',
                }}>
                  {cred.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div style={styles.credentialDetails}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Exchange</span>
                  <span style={styles.detailValue}>{cred.exchangeName}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>API Key</span>
                  <span style={styles.detailValue}>{cred.apiKeyName}</span>
                </div>
              </div>
              <div style={styles.credentialActions}>
                {cred.isActive ? (
                  <button onClick={() => handleDeactivate(cred.exchangeCredentialId)} style={styles.deactivateButton}>
                    Deactivate
                  </button>
                ) : (
                  <button onClick={() => handleActivate(cred.exchangeCredentialId)} style={styles.activateButton}>
                    Activate
                  </button>
                )}
                <button onClick={() => handleDeleteCredential(cred.exchangeCredentialId)} style={styles.deleteButton}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderDefaultsTab = () => (
    <div>
      <h3 style={styles.tabTitle}>Default Values</h3>
      {settings && (
        <form onSubmit={handleSaveDefaults} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Default Investment Amount</label>
            <input
              type="number"
              value={settings.defaultInvestment}
              onChange={e => setSettings({ ...settings, defaultInvestment: parseFloat(e.target.value) || 0 })}
              style={styles.input}
              step="0.01"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Default Interval (hours)</label>
            <input
              type="number"
              value={settings.defaultIntervalHours}
              onChange={e => setSettings({ ...settings, defaultIntervalHours: parseInt(e.target.value) || 24 })}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Default Quote Currency</label>
            <select
              value={settings.defaultQuoteCurrency}
              onChange={e => setSettings({ ...settings, defaultQuoteCurrency: e.target.value })}
              style={styles.select}
            >
              <option value="USD">USD</option>
              <option value="USDT">USDT</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
          <button type="submit" style={styles.saveButton}>Save Defaults</button>
        </form>
      )}
    </div>
  );

  const renderRiskTab = () => (
    <div>
      <h3 style={styles.tabTitle}>Risk Parameters</h3>
      {settings && (
        <form onSubmit={handleSaveRisk} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.enableRiskChecks}
                onChange={e => setSettings({ ...settings, enableRiskChecks: e.target.checked })}
                style={styles.checkbox}
              />
              Enable Risk Checks
            </label>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Max Position Size (USD)</label>
            <input
              type="number"
              value={settings.maxPositionSize || ''}
              onChange={e => setSettings({ ...settings, maxPositionSize: e.target.value ? parseFloat(e.target.value) : null })}
              style={styles.input}
              step="0.01"
              placeholder="Leave empty for unlimited"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Default Stop Loss (%)</label>
            <input
              type="number"
              value={settings.defaultStopLossPercent || ''}
              onChange={e => setSettings({ ...settings, defaultStopLossPercent: e.target.value ? parseFloat(e.target.value) : null })}
              style={styles.input}
              step="0.1"
              placeholder="e.g., 5 for 5%"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Max Leverage</label>
            <input
              type="number"
              value={settings.maxLeverage || ''}
              onChange={e => setSettings({ ...settings, maxLeverage: e.target.value ? parseInt(e.target.value) : null })}
              style={styles.input}
              placeholder="e.g., 10"
            />
          </div>
          <button type="submit" style={styles.saveButton}>Save Risk Settings</button>
        </form>
      )}
    </div>
  );

  const renderNotificationsTab = () => (
    <div>
      <h3 style={styles.tabTitle}>Notifications</h3>
      {settings && (
        <form onSubmit={handleSaveNotifications} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.notifyOnOrderFill}
                onChange={e => setSettings({ ...settings, notifyOnOrderFill: e.target.checked })}
                style={styles.checkbox}
              />
              Notify on Order Fill
            </label>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.notifyOnError}
                onChange={e => setSettings({ ...settings, notifyOnError: e.target.checked })}
                style={styles.checkbox}
              />
              Notify on Error
            </label>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.notifyOnPositionClose}
                onChange={e => setSettings({ ...settings, notifyOnPositionClose: e.target.checked })}
                style={styles.checkbox}
              />
              Notify on Position Close
            </label>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Notification Email</label>
            <input
              type="email"
              value={settings.notificationEmail || ''}
              onChange={e => setSettings({ ...settings, notificationEmail: e.target.value || null })}
              style={styles.input}
              placeholder="your@email.com"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Webhook URL</label>
            <input
              type="url"
              value={settings.webhookUrl || ''}
              onChange={e => setSettings({ ...settings, webhookUrl: e.target.value || null })}
              style={styles.input}
              placeholder="https://..."
            />
          </div>
          <button type="submit" style={styles.saveButton}>Save Notifications</button>
        </form>
      )}
    </div>
  );

  const renderEmailServiceTab = () => {
    try {
      return (
        <div>
          <div style={styles.tabHeader}>
            <h3 style={styles.tabTitle}>Email Service</h3>
            <button onClick={() => setShowNewEmailConfigForm(true)} style={styles.addButton}>
              + Add Email Service
            </button>
          </div>

      {showNewEmailConfigForm && (
        <form onSubmit={handleCreateEmailConfig} style={styles.form}>
          <h4 style={styles.formTitle}>New Email Service Configuration</h4>
          <div style={styles.formGroup}>
            <label style={styles.label}>Service Type</label>
            <select
              value={newEmailConfig.serviceType}
              onChange={e => setNewEmailConfig({ ...newEmailConfig, serviceType: e.target.value })}
              style={styles.select}
            >
              {emailServiceTypes.length > 0 ? (
                emailServiceTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))
              ) : (
                <option value="Mailgun">Mailgun</option>
              )}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Display Name</label>
            <input
              type="text"
              value={newEmailConfig.displayName}
              onChange={e => setNewEmailConfig({ ...newEmailConfig, displayName: e.target.value })}
              style={styles.input}
              placeholder="e.g., My Mailgun Account"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>API Key</label>
            <input
              type="password"
              value={newEmailConfig.apiKey}
              onChange={e => setNewEmailConfig({ ...newEmailConfig, apiKey: e.target.value })}
              style={styles.input}
              placeholder="Your Mailgun API key"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Domain</label>
            <input
              type="text"
              value={newEmailConfig.domain || ''}
              onChange={e => setNewEmailConfig({ ...newEmailConfig, domain: e.target.value })}
              style={styles.input}
              placeholder="e.g., mg.yourdomain.com"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Email</label>
            <input
              type="email"
              value={newEmailConfig.fromEmail}
              onChange={e => setNewEmailConfig({ ...newEmailConfig, fromEmail: e.target.value })}
              style={styles.input}
              placeholder="noreply@yourdomain.com"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Name (optional)</label>
            <input
              type="text"
              value={newEmailConfig.fromName || ''}
              onChange={e => setNewEmailConfig({ ...newEmailConfig, fromName: e.target.value })}
              style={styles.input}
              placeholder="Uliji Trading"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Region</label>
            <select
              value={newEmailConfig.region || 'US'}
              onChange={e => setNewEmailConfig({ ...newEmailConfig, region: e.target.value })}
              style={styles.select}
            >
              <option value="US">US</option>
              <option value="EU">EU</option>
            </select>
          </div>
          <div style={styles.formActions}>
            <button type="submit" style={styles.saveButton}>Create</button>
            <button type="button" onClick={() => setShowNewEmailConfigForm(false)} style={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {emailConfigs.length === 0 ? (
        <div style={styles.empty}>No email service configured. Add one to enable email notifications.</div>
      ) : (
        <div style={styles.credentialsList}>
          {emailConfigs.map(config => (
            <div key={config.emailServiceConfigId} style={styles.credentialCard}>
              <div style={styles.credentialHeader}>
                <span style={styles.credentialName}>{config.displayName}</span>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: config.isActive ? '#4caf50' : '#9e9e9e',
                }}>
                  {config.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div style={styles.credentialDetails}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Service</span>
                  <span style={styles.detailValue}>{config.serviceType}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Domain</span>
                  <span style={styles.detailValue}>{config.domain}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>From</span>
                  <span style={styles.detailValue}>
                    {config.fromName ? `${config.fromName} <${config.fromEmail}>` : config.fromEmail}
                  </span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Region</span>
                  <span style={styles.detailValue}>{config.region || 'US'}</span>
                </div>
              </div>

              {/* Test Email Section */}
              <div style={styles.testEmailSection}>
                <input
                  type="email"
                  value={testEmailAddress}
                  onChange={e => setTestEmailAddress(e.target.value)}
                  style={{ ...styles.input, flex: 1 }}
                  placeholder="Enter email to test..."
                />
                <button
                  onClick={() => handleTestEmail(config.emailServiceConfigId)}
                  style={styles.testButton}
                  disabled={testingConfigId === config.emailServiceConfigId}
                >
                  {testingConfigId === config.emailServiceConfigId ? 'Sending...' : 'Send Test'}
                </button>
              </div>

              <div style={styles.credentialActions}>
                {config.isActive ? (
                  <button onClick={() => handleDeactivateEmailConfig(config.emailServiceConfigId)} style={styles.deactivateButton}>
                    Deactivate
                  </button>
                ) : (
                  <button onClick={() => handleActivateEmailConfig(config.emailServiceConfigId)} style={styles.activateButton}>
                    Activate
                  </button>
                )}
                <button onClick={() => handleDeleteEmailConfig(config.emailServiceConfigId)} style={styles.deleteButton}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
      );
    } catch (err) {
      console.error('Error rendering email service tab:', err);
      return (
        <div style={styles.error}>
          Error loading email service configuration. Please refresh the page.
        </div>
      );
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo} onClick={() => navigate('/')}>Uliji</h1>
          <h2 style={styles.pageTitle}>Settings</h2>
        </div>
        <div style={styles.userInfo}>
          {isAdmin && <button onClick={() => navigate('/admin/users')} style={styles.usersButton}>Manage Users</button>}
          <span style={styles.username}>{user?.username}</span>
          <button onClick={logout} style={styles.logoutButton}>Logout</button>
        </div>
      </header>

      <main style={styles.main}>
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        <div style={styles.tabs}>
          <button
            onClick={() => setActiveTab('credentials')}
            style={{ ...styles.tab, ...(activeTab === 'credentials' ? styles.activeTab : {}) }}
          >
            Exchange Credentials
          </button>
          <button
            onClick={() => setActiveTab('defaults')}
            style={{ ...styles.tab, ...(activeTab === 'defaults' ? styles.activeTab : {}) }}
          >
            Default Values
          </button>
          <button
            onClick={() => setActiveTab('risk')}
            style={{ ...styles.tab, ...(activeTab === 'risk' ? styles.activeTab : {}) }}
          >
            Risk Parameters
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            style={{ ...styles.tab, ...(activeTab === 'notifications' ? styles.activeTab : {}) }}
          >
            Notifications
          </button>
          <button
            onClick={() => setActiveTab('email')}
            style={{ ...styles.tab, ...(activeTab === 'email' ? styles.activeTab : {}) }}
          >
            Email Service
          </button>
        </div>

        <div style={styles.tabContent}>
          {isLoading ? (
            <div style={styles.loading}>Loading...</div>
          ) : (
            <>
              {activeTab === 'credentials' && renderCredentialsTab()}
              {activeTab === 'defaults' && renderDefaultsTab()}
              {activeTab === 'risk' && renderRiskTab()}
              {activeTab === 'notifications' && renderNotificationsTab()}
              {activeTab === 'email' && renderEmailServiceTab()}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 32px',
    backgroundColor: '#16213e',
    borderBottom: '1px solid #233554',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  logo: {
    color: '#e94560',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
    cursor: 'pointer',
  },
  pageTitle: {
    color: '#ccd6f6',
    fontSize: '18px',
    fontWeight: 'normal',
    margin: 0,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  username: {
    color: '#ccd6f6',
  },
  logoutButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #e94560',
    backgroundColor: 'transparent',
    color: '#e94560',
    cursor: 'pointer',
    fontSize: '14px',
  },
  usersButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #9c27b0',
    backgroundColor: 'transparent',
    color: '#9c27b0',
    cursor: 'pointer',
    fontSize: '14px',
  },
  main: {
    padding: '32px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  error: {
    backgroundColor: 'rgba(233, 69, 96, 0.2)',
    border: '1px solid #e94560',
    color: '#e94560',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  success: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    border: '1px solid #4caf50',
    color: '#4caf50',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    borderBottom: '1px solid #233554',
    paddingBottom: '0',
  },
  tab: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '14px',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
  },
  activeTab: {
    color: '#e94560',
    borderBottom: '2px solid #e94560',
  },
  tabContent: {
    backgroundColor: '#16213e',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #233554',
  },
  tabHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  tabTitle: {
    color: '#ccd6f6',
    fontSize: '18px',
    margin: 0,
  },
  addButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#e94560',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
  },
  loading: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '48px',
  },
  empty: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '48px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formTitle: {
    color: '#ccd6f6',
    fontSize: '16px',
    margin: '0 0 8px 0',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    color: '#8892b0',
    fontSize: '14px',
  },
  input: {
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #233554',
    backgroundColor: '#1a1a2e',
    color: '#ccd6f6',
    fontSize: '14px',
  },
  select: {
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #233554',
    backgroundColor: '#1a1a2e',
    color: '#ccd6f6',
    fontSize: '14px',
  },
  textarea: {
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #233554',
    backgroundColor: '#1a1a2e',
    color: '#ccd6f6',
    fontSize: '14px',
    fontFamily: 'monospace',
    resize: 'vertical',
  },
  checkbox: {
    marginRight: '8px',
  },
  checkboxLabel: {
    color: '#ccd6f6',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  saveButton: {
    padding: '10px 24px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#4caf50',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  cancelButton: {
    padding: '10px 24px',
    borderRadius: '6px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '14px',
  },
  credentialsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  credentialCard: {
    backgroundColor: '#1a1a2e',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #233554',
  },
  credentialHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  credentialName: {
    color: '#ccd6f6',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    color: 'white',
    fontWeight: 'bold',
  },
  credentialDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailLabel: {
    color: '#8892b0',
    fontSize: '13px',
  },
  detailValue: {
    color: '#ccd6f6',
    fontSize: '13px',
  },
  credentialActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #233554',
  },
  activateButton: {
    padding: '6px 16px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#4caf50',
    color: 'white',
    cursor: 'pointer',
    fontSize: '13px',
  },
  deactivateButton: {
    padding: '6px 16px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#ff9800',
    color: 'white',
    cursor: 'pointer',
    fontSize: '13px',
  },
  deleteButton: {
    padding: '6px 16px',
    borderRadius: '4px',
    border: '1px solid #f44336',
    backgroundColor: 'transparent',
    color: '#f44336',
    cursor: 'pointer',
    fontSize: '13px',
  },
  testEmailSection: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #233554',
  },
  testButton: {
    padding: '10px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#2196f3',
    color: 'white',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap' as const,
  },
};
